import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
  TableView as TableViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { toyService } from '../../services/toyService';
import { currencyService } from '../../services/currencyService';
import { useTranslation } from '../../hooks/useTranslation';

function escapeCsvValue(value) {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).replace(/"/g, '""');
  return /[",\n]/.test(stringValue) ? `"${stringValue}"` : stringValue;
}

function CollectionExport() {
  const { currentUser } = useAuth();
  const { t, currentLanguage } = useTranslation();
  const [toys, setToys] = useState([]);
  const [userCurrency, setUserCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);

  useEffect(() => {
    async function loadData() {
      if (!currentUser?.uid) {
        setErrorKey('authRequired');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [toyData, currency] = await Promise.all([
          toyService.getToys(currentUser.uid),
          currencyService.getUserCurrency(currentUser.uid)
        ]);
        setToys(toyData);
        setUserCurrency(currency || 'USD');
        setErrorKey(null);
      } catch (err) {
        console.error('Failed to load export data:', err);
        setErrorKey('loadError');
      } finally {
        setLoading(false);
      }
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentLanguage]);

  const exportRows = useMemo(() => (
    toys.map((toy) => ({
      name: toy.name || '',
      brand: toy.brand || '',
      series: toy.series || '',
      type: toy.type || '',
      scale: toy.scale || '',
      code: toy.code || '',
      quantity: toy.qty || 1,
      price: toy.price || 0,
      preorderPrice: toy.preorder_price || 0,
      source: toy.source || '',
      note: toy.note || '',
      createdDate: toy.createdDate || '',
      priceCurrency: toy.priceCurrency || userCurrency,
      preorderPriceCurrency: toy.preorderPriceCurrency || userCurrency
    }))
  ), [toys, userCurrency]);

  const tablePreview = exportRows.slice(0, 5);

  const headers = useMemo(() => ([
    { key: 'name', label: t('collection.export.headers.name') },
    { key: 'brand', label: t('collection.export.headers.brand') },
    { key: 'series', label: t('collection.export.headers.series') },
    { key: 'type', label: t('collection.export.headers.type') },
    { key: 'scale', label: t('collection.export.headers.scale') },
    { key: 'code', label: t('collection.export.headers.code') },
    { key: 'quantity', label: t('collection.export.headers.quantity') },
    { key: 'price', label: t('collection.export.headers.price') },
    { key: 'priceCurrency', label: t('collection.export.headers.priceCurrency') },
    { key: 'preorderPrice', label: t('collection.export.headers.preorderPrice') },
    { key: 'preorderPriceCurrency', label: t('collection.export.headers.preorderPriceCurrency') },
    { key: 'source', label: t('collection.export.headers.source') },
    { key: 'note', label: t('collection.export.headers.note') },
    { key: 'createdDate', label: t('collection.export.headers.createdDate') }
  ]), [t]);

  function downloadBlob(filename, blob) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  function handleDownloadCsv() {
    if (!exportRows.length) return;

    const csvLines = [headers.map((header) => header.label).join(',')];
    exportRows.forEach((row) => {
      const values = headers.map((header) => escapeCsvValue(row[header.key]));
      csvLines.push(values.join(','));
    });

    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const timestamp = format(new Date(), 'yyyyMMdd-HHmm');
    downloadBlob(`toyport-collection-${timestamp}.csv`, blob);
  }

  function handleDownloadJson() {
    if (!exportRows.length) return;

    const payload = {
      exportedAt: new Date().toISOString(),
      currency: userCurrency,
      items: exportRows
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8;' });
    const timestamp = format(new Date(), 'yyyyMMdd-HHmm');
    downloadBlob(`toyport-collection-${timestamp}.json`, blob);
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {t('collection.export.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('collection.export.subtitle')}
            </Typography>
          </Box>

          {errorKey && (
            <Alert severity="error">{t(`collection.export.${errorKey}`)}</Alert>
          )}

          {!errorKey && !exportRows.length && (
            <Alert severity="info">{t('collection.export.empty')}</Alert>
          )}

          {!errorKey && (
            <Card>
              <CardHeader
                title={t('collection.export.actionsTitle')}
                subheader={t('collection.export.actionsSubtitle', { count: exportRows.length })}
              />
              <Divider />
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadCsv}
                    disabled={!exportRows.length}
                  >
                    {t('collection.export.downloadCsv')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DescriptionIcon />}
                    onClick={handleDownloadJson}
                    disabled={!exportRows.length}
                  >
                    {t('collection.export.downloadJson')}
                  </Button>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
                  {t('collection.export.fileHint')}
                </Typography>
              </CardContent>
            </Card>
          )}

          {!errorKey && exportRows.length > 0 && (
            <Card>
              <CardHeader
                title={t('collection.export.previewTitle', { count: tablePreview.length })}
                subheader={t('collection.export.previewSubtitle', { total: exportRows.length, currency: userCurrency })}
                avatar={<TableViewIcon color="primary" />}
              />
              <Divider />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {headers.map((header) => (
                          <TableCell key={header.key}>{header.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tablePreview.map((row, index) => (
                        <TableRow key={`preview-row-${index}`}>
                          {headers.map((header) => (
                            <TableCell key={header.key}>
                              {header.key === 'price' || header.key === 'preorderPrice'
                                ? Math.round(row[header.key] || 0).toLocaleString()
                                : row[header.key] || ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {exportRows.length > tablePreview.length && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    {t('collection.export.previewFooter', { remaining: exportRows.length - tablePreview.length })}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

export default CollectionExport;
